import { Sel, STagType } from './tag.js';

/**
 * Builds a selector for querying elements by STag (original implementation)
 *
 * @param location - The location category from the Sel enum
 * @param tag - The specific element tag
 * @param uniqueID - Optional unique identifier (for elements in lists)
 * @returns A CSS selector string for the specified STag
 */
export const buildSTagSelector = (location: Sel, tag: string, uniqueID?: string): string => {
  const baseTag = [location, tag].join('.');
  const uniqueTag = uniqueID ? `${baseTag}:${uniqueID}` : baseTag;

  return `[data-prog-key="${uniqueTag.toLowerCase()}"]`;
};

/**
 * Builds a selector for querying elements by STagNew
 *
 * @param location - The location category from the Sel enum
 * @param tag - The specific element tag
 * @param uniqueID - Optional unique identifier (for elements in lists)
 * @param type - Optional tag type (interactive or data)
 * @returns A CSS selector string for the specified STagNew
 */
export const buildSTagNewSelector = (
  location: Sel,
  tag: string,
  uniqueID?: string,
  type: STagType = STagType.Interactive
): string => {
  const baseTag = [location, tag].join('.');
  const uniqueTag = uniqueID ? `${baseTag}:${uniqueID}` : baseTag;
  const tagWithType = `${type}:${uniqueTag.toLowerCase()}`;

  return `[data-stag="${tagWithType}"]`;
};

/**
 * Builds a selector for querying data elements by STag
 *
 * @param location - The location category from the Sel enum
 * @param tag - The specific element tag
 * @param uniqueID - Optional unique identifier (for elements in lists)
 * @returns A CSS selector string for the specified DataSTag
 */
export const buildDataSTagSelector = (location: Sel, tag: string, uniqueID?: string): string => {
  return buildSTagNewSelector(location, tag, uniqueID, STagType.Data);
};

/**
 * Queries the document for elements with the specified STag (original implementation)
 *
 * @param location - The location category from the Sel enum
 * @param tag - The specific element tag
 * @param uniqueID - Optional unique identifier (for elements in lists)
 * @returns An array of elements matching the STag
 */
export const querySTagElements = (location: Sel, tag: string, uniqueID?: string): Element[] => {
  const selector = buildSTagSelector(location, tag, uniqueID);
  return Array.from(document.querySelectorAll(selector));
};

/**
 * Queries the document for elements with the specified STagNew
 *
 * @param location - The location category from the Sel enum
 * @param tag - The specific element tag
 * @param uniqueID - Optional unique identifier (for elements in lists)
 * @param type - Optional tag type (interactive or data)
 * @returns An array of elements matching the STagNew
 */
export const querySTagNewElements = (
  location: Sel,
  tag: string,
  uniqueID?: string,
  type: STagType = STagType.Interactive
): Element[] => {
  const selector = buildSTagNewSelector(location, tag, uniqueID, type);
  return Array.from(document.querySelectorAll(selector));
};

/**
 * Queries the document for data elements with the specified STag
 *
 * @param location - The location category from the Sel enum
 * @param tag - The specific element tag
 * @param uniqueID - Optional unique identifier (for elements in lists)
 * @returns An array of elements matching the DataSTag
 */
export const queryDataSTagElements = (location: Sel, tag: string, uniqueID?: string): Element[] => {
  return querySTagNewElements(location, tag, uniqueID, STagType.Data);
};

/**
 * Gets the first element with the specified STag (original implementation)
 *
 * @param location - The location category from the Sel enum
 * @param tag - The specific element tag
 * @param uniqueID - Optional unique identifier (for elements in lists)
 * @returns The first element matching the STag, or null if none found
 */
export const getSTagElement = (location: Sel, tag: string, uniqueID?: string): Element | null => {
  const selector = buildSTagSelector(location, tag, uniqueID);
  return document.querySelector(selector);
};

/**
 * Gets the first element with the specified STagNew
 *
 * @param location - The location category from the Sel enum
 * @param tag - The specific element tag
 * @param uniqueID - Optional unique identifier (for elements in lists)
 * @param type - Optional tag type (interactive or data)
 * @returns The first element matching the STagNew, or null if none found
 */
export const getSTagNewElement = (
  location: Sel,
  tag: string,
  uniqueID?: string,
  type: STagType = STagType.Interactive
): Element | null => {
  const selector = buildSTagNewSelector(location, tag, uniqueID, type);
  return document.querySelector(selector);
};

/**
 * Gets the first data element with the specified STag
 *
 * @param location - The location category from the Sel enum
 * @param tag - The specific element tag
 * @param uniqueID - Optional unique identifier (for elements in lists)
 * @returns The first element matching the DataSTag, or null if none found
 */
export const getDataSTagElement = (
  location: Sel,
  tag: string,
  uniqueID?: string
): Element | null => {
  return getSTagNewElement(location, tag, uniqueID, STagType.Data);
};
